import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AlertTriangle } from 'lucide-react';
import { useCanEdit, useDeck } from './store';
import { HistoryButton } from './HistoryButton';

interface Props {
  k: string;
  ph: string;
  max: number;
  size: number;
  min?: number;
  className?: string;
  boxClass?: string;
  testId: string;
  metaDark?: boolean;
  onCommit?: (text: string) => void;
}

export const ago = (at?: string) => (at ? formatDistanceToNow(new Date(at), { addSuffix: true, locale: fr }) : '');

export function Editable({ k, ph, max, size, min, className = '', boxClass = '', testId, metaDark, onCommit }: Props) {
  const { get, set, meta } = useDeck();
  const canEdit = useCanEdit();
  const value: string = (get(k) as string) || '';
  const box = useRef<HTMLDivElement>(null);
  const el = useRef<HTMLDivElement>(null);
  const [len, setLen] = useState(value.length);
  const [over, setOver] = useState(false);
  const minSize = min ?? Math.max(11, Math.round(size * 0.55));

  const fit = useCallback(() => {
    const e = el.current;
    const b = box.current;
    if (!e || !b) return;
    let s = size;
    e.style.fontSize = `${s}px`;
    while (s > minSize && e.offsetHeight > b.clientHeight) {
      s -= 1;
      e.style.fontSize = `${s}px`;
    }
    setOver(e.offsetHeight > b.clientHeight + 1);
  }, [size, minSize]);

  useLayoutEffect(() => {
    const e = el.current;
    if (!e) return;
    if (document.activeElement !== e && e.innerText !== value) e.innerText = value;
    setLen(value.length);
    fit();
  }, [value, fit]);

  useEffect(() => {
    (document as any).fonts?.ready?.then(fit);
    const ro = new ResizeObserver(fit);
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, [fit]);

  const onInput = () => {
    const e = el.current!;
    let t = e.innerText;
    if (t.trim() === '') { t = ''; e.textContent = ''; }
    setLen(t.length);
    fit();
    set(k, t);
  };

  const onPaste = (ev: React.ClipboardEvent) => {
    ev.preventDefault();
    document.execCommand('insertText', false, ev.clipboardData.getData('text/plain'));
  };

  const m = meta(k);
  const ratio = len / max;
  const warn = over || ratio >= 0.85;

  return (
    <div ref={box} className={`relative min-h-0 min-w-0 ${boxClass}`} data-editable={canEdit} data-over={canEdit && over} style={{ overflow: canEdit ? 'visible' : 'hidden' }}>
      <div className="h-full w-full overflow-hidden">
        <div
          ref={el}
          data-testid={testId}
          data-ph={ph}
          contentEditable={canEdit ? ('plaintext-only' as any) : false}
          suppressContentEditableWarning
          spellCheck
          onInput={onInput}
          onPaste={onPaste}
          onBlur={() => onCommit?.(el.current?.innerText.trim() || '')}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') el.current?.blur(); }}
          className={`ed outline-none whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${className}`}
          style={{ fontSize: size }}
        />
      </div>
      {canEdit && <HistoryButton k={k} testId={testId} />}
      {canEdit && (
        <div
          className={`ed-meta pointer-events-none absolute bottom-1 right-1 z-30 flex items-center gap-2 rounded-full px-2.5 py-0.5 font-ui text-[12px] whitespace-nowrap shadow ${metaDark ? 'bg-white text-neutral-800' : 'bg-neutral-900 text-white'}`}
          style={over ? { opacity: 1 } : undefined}
        >
          {over && <span className="flex items-center gap-1 text-red-500 font-semibold" data-testid={`${testId}-overflow-alert`}><AlertTriangle size={12} /> Texte trop long : raccourcissez</span>}
          <span data-testid={`${testId}-counter`} className={warn ? (ratio >= 1 || over ? 'text-red-500 font-semibold' : 'text-orange-400 font-semibold') : ''}>{len} / {max}</span>
          {m?.by && m?.at && <span className="opacity-70">modifié par {m.by} {ago(m.at)}</span>}
        </div>
      )}
    </div>
  );
}
